import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import type { PathSegment, RouteStructure, TrailerPath } from '../utils/routeInference';

export interface TripTimelineEvent {
    id: string | null; // null = HQ
    label: string;
    startDate: Date;
    endDate: Date;
}

export interface TripTimelineProps {
    /** Events in chronological order (e.g. trip events with dates) */
    events: TripTimelineEvent[];
    /** Optional trailer paths to overlay as routes on the timeline */
    routeStructure?: RouteStructure | null;
    getTrailerName?: (trailerId: string) => string;
    /** Total width of the timeline area */
    width?: number;
    /** Height per row (event bar) */
    rowHeight?: number;
}

const ROW_HEIGHT = 36;
const LABEL_WIDTH = 140;
const MIN_BAR_WIDTH = 8;
/** Horizontal padding for the time axis so first/last date labels are not cut off */
const CHART_PAD_X = 28;
const TICK_COUNT = 6;
const AXIS_HEIGHT = 36;

/**
 * Shared time range and x-scale for both timelines so dates align.
 * Time range is computed from events and all trailer path segments.
 */
function useSharedTimeRange(
    events: TripTimelineEvent[],
    trailerPaths: { segments: PathSegment[] }[],
    width: number
): { timeMin: number; timeRange: number; xForTime: (t: number) => number } {
    return useMemo(() => {
        const chartWidth = width - LABEL_WIDTH - 2 * CHART_PAD_X;
        let min = Infinity;
        let max = -Infinity;
        for (const e of events) {
            const s = e.startDate.getTime();
            const end = e.endDate.getTime();
            if (s < min) min = s;
            if (end > max) max = end;
        }
        for (const path of trailerPaths) {
            for (const seg of path.segments) {
                const s = seg.startDate.getTime();
                const end = seg.endDate.getTime();
                if (s < min) min = s;
                if (end > max) max = end;
            }
        }
        if (events.length === 0 && trailerPaths.length === 0) {
            min = 0;
            max = 86400000;
        }
        if (min >= max) max = min + 86400000;
        const timeRange = max - min;

        const xForTime = (t: number) => {
            if (timeRange <= 0) return LABEL_WIDTH + CHART_PAD_X;
            return LABEL_WIDTH + CHART_PAD_X + ((t - min) / timeRange) * chartWidth;
        };

        return { timeMin: min, timeRange, xForTime };
    }, [events, trailerPaths, width]);
}

/**
 * Renders the date axis (ticks + labels) and vertical grid lines for one timeline.
 * Same style for both timelines; call with same timeMin/timeRange/xForTime so dates align.
 */
function DateAxis({
    timeMin,
    timeRange,
    xForTime,
    width: _width,
    contentHeight,
}: {
    timeMin: number;
    timeRange: number;
    xForTime: (t: number) => number;
    width: number;
    contentHeight: number;
}) {
    const ticks = Array.from({ length: TICK_COUNT }, (_, i) => {
        const t = timeMin + (i / (TICK_COUNT - 1)) * timeRange;
        const x = xForTime(t);
        return (
            <g key={i}>
                <line
                    x1={x}
                    y1={AXIS_HEIGHT - 12}
                    x2={x}
                    y2={AXIS_HEIGHT + contentHeight}
                    stroke="#e0e0e0"
                    strokeWidth={1}
                    strokeDasharray="2 2"
                />
                <text x={x} y={AXIS_HEIGHT - 16} textAnchor="middle" fontSize={10} fill="#666">
                    {new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </text>
            </g>
        );
    });
    return <>{ticks}</>;
}

/**
 * Gantt-style timelines: one for events, one for trailers. Same time range and width so dates align.
 * Subsection headers differentiate the two; styles match (font sizes etc.), only bar colors differ.
 */
export default function TripTimeline({
    events,
    routeStructure,
    getTrailerName = (id: string) => `Trailer ${id.slice(0, 8)}`,
    width = 600,
    rowHeight = ROW_HEIGHT,
}: TripTimelineProps) {
    const trailerPaths = useMemo(() => {
        if (!routeStructure || routeStructure.trailerPaths.size === 0) return [];
        const paths: TrailerPath[] = [];
        for (const path of Array.from(routeStructure.trailerPaths.values())) paths.push(path);
        return paths;
    }, [routeStructure]);

    const { timeMin, timeRange, xForTime } = useSharedTimeRange(events, trailerPaths, width);

    if (events.length === 0 && trailerPaths.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No events or trailers with dates to display.
            </Typography>
        );
    }

    const eventsSvgHeight = events.length > 0 ? AXIS_HEIGHT + 24 + events.length * rowHeight : 0;
    const trailersSvgHeight = trailerPaths.length > 0 ? AXIS_HEIGHT + 24 + trailerPaths.length * (rowHeight * 0.8) : 0;

    const eventRows =
        events.length > 0
            ? events.map((evt, index) => {
                  const left = xForTime(evt.startDate.getTime());
                  const right = xForTime(evt.endDate.getTime());
                  const barWidth = Math.max(right - left, MIN_BAR_WIDTH);
                  const y = AXIS_HEIGHT + 24 + index * rowHeight - 10 + 10;
                  return (
                      <g key={evt.id ?? 'hq'}>
                          <rect
                              x={left}
                              y={AXIS_HEIGHT + 24 + index * rowHeight - 10}
                              width={barWidth}
                              height={20}
                              rx={4}
                              fill="#1976d2"
                              fillOpacity={0.8}
                          />
                          <text x={LABEL_WIDTH - 8} y={y + 2} textAnchor="end" fontSize={12} fill="#333">
                              {evt.label.length > 18 ? evt.label.slice(0, 18) + '…' : evt.label}
                          </text>
                      </g>
                  );
              })
            : [];

    const trailerRows =
        trailerPaths.length > 0
            ? trailerPaths.map((path, pathIndex) => {
                  const rowY = AXIS_HEIGHT + 24 + pathIndex * (rowHeight * 0.8);
                  const eventSegments = path.segments.filter(
                      (seg: PathSegment): seg is PathSegment & { kind: 'event' } => seg.kind === 'event'
                  );
                  return (
                      <g key={path.trailerId}>
                          <text x={LABEL_WIDTH - 8} y={rowY + 2} textAnchor="end" fontSize={11} fill="#666">
                              {getTrailerName(path.trailerId)}
                          </text>
                          {eventSegments.map((seg, i) => {
                              const left = xForTime(seg.startDate.getTime());
                              const right = xForTime(seg.endDate.getTime());
                              const w = Math.max(right - left, 4);
                              return <rect key={i} x={left} y={rowY - 8} width={w} height={14} rx={2} fill="#2e7d32" fillOpacity={0.6} />;
                          })}
                      </g>
                  );
              })
            : [];

    const eventContentHeight = events.length > 0 ? 24 + events.length * rowHeight : 0;
    const trailerContentHeight = trailerPaths.length > 0 ? 24 + trailerPaths.length * (rowHeight * 0.8) : 0;

    return (
        <Box sx={{ overflowX: 'auto', overflowY: 'visible', width: '100%', minWidth: 0 }}>
            <Box sx={{ minWidth: width }}>
                {events.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Events
                        </Typography>
                        <svg width={width} height={eventsSvgHeight} style={{ display: 'block' }}>
                            <DateAxis
                                timeMin={timeMin}
                                timeRange={timeRange}
                                xForTime={xForTime}
                                width={width}
                                contentHeight={eventContentHeight}
                            />
                            {eventRows}
                        </svg>
                    </Box>
                )}
                {trailerPaths.length > 0 && (
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Trailers
                        </Typography>
                        <svg width={width} height={trailersSvgHeight} style={{ display: 'block' }}>
                            <DateAxis
                                timeMin={timeMin}
                                timeRange={timeRange}
                                xForTime={xForTime}
                                width={width}
                                contentHeight={trailerContentHeight}
                            />
                            {trailerRows}
                        </svg>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
